<script setup>
import { ref, watch, onMounted, computed } from "vue";
import { getProductDetails } from "../db/inventory.js";
import { addToCart, cartState } from "../store/cart.js";

const props = defineProps({
  productId: { type: Number, required: true },
  open: { type: Boolean, default: false },
});
const emit = defineEmits(["close"]);

const loading = ref(false);
const data = ref(null);
const mainImage = ref(null);
const justAdded = ref(false);
const qty = ref(1);
const maxQty = computed(() => Math.max(1, Number(data.value?.stock_qty || 1)));

const priceWithDiscount = computed(() => {
  const p = Number(data.value?.unit_price || 0);
  const d = Math.min(Math.max(Number(data.value?.discount || 0), 0), 100);
  const discounted = p * (1 - d / 100);
  const tr = data.value?.tax_rate;
  return tr != null ? discounted * (1 + Number(tr) / 100) : discounted;
});
const hasDiscount = computed(() => Number(data.value?.discount || 0) > 0);
const hasTax = computed(() => {
  const tr = data.value?.tax_rate;
  const n = Number(tr);
  return !Number.isNaN(n) && n > 0;
});

// Total local: precio (con descuento/IVA) por cantidad seleccionada
const selectedTotal = computed(() => {
  const unit = Number(priceWithDiscount.value || 0);
  const q = Math.max(1, Number(qty.value || 1));
  return unit * q;
});

// Flag de poco stock: verdadero si el stock es menor o igual al punto de reorden
const isLowStock = computed(() => {
  const stock = Number(data.value?.stock_qty || 0);
  const reorder = Number(data.value?.reorder_level || 0);
  return reorder > 0 && stock <= reorder;
});

// Chips superiores (como en el ejemplo): SKU, Stock, Precio (+ IVA si aplica)
const topChips = computed(() => {
  const sku = data.value?.sku || "-";
  const stock = Number(data.value?.stock_qty || 0);
  const tr = data.value?.tax_rate;
  const d = Math.min(Math.max(Number(data.value?.discount || 0), 0), 100);
  const unit = Number(data.value?.unit_price || 0);
  const discounted = unit * (1 - d / 100);
  const taxMultiplier = tr != null ? 1 + Number(tr) / 100 : 1;
  const finalPrice = (discounted * taxMultiplier).toFixed(2);
  return [
    { label: "SKU:", value: sku },
    { label: "Stock:", value: stock },
    {
      label: hasTax.value ? "Precio + IVA incluido:" : "Precio:",
      value: `$${finalPrice}`,
    },
  ];
});

// Descripción en bullets
const descBullets = computed(() => {
  const t = String(data.value?.description || "").trim();
  if (!t) return [];
  return t
    .split(/\.|\n|;|•/)
    .map((s) => s.trim())
    .filter(Boolean);
});

// Tabla de detalles al final (refleja descuento y IVA si aplica)
const detailsTable = computed(() => {
  const unit = Number(data.value?.unit_price || 0);
  const d = Math.min(Math.max(Number(data.value?.discount || 0), 0), 100);
  const has = d > 0;
  const discounted = unit * (1 - d / 100);
  const tr = data.value?.tax_rate;
  const taxMultiplier = tr != null ? 1 + Number(tr) / 100 : 1;
  const finalWithTax = discounted * taxMultiplier;
  const finalNoDiscountWithTax = unit * taxMultiplier;

  return [
    {
      label: hasTax.value ? "Precio + IVA incluido" : "Precio",
      value: has
        ? `$${finalWithTax.toFixed(2)} (-${d}%)`
        : `$${finalNoDiscountWithTax.toFixed(2)}`,
      old: has ? `$${finalNoDiscountWithTax.toFixed(2)}` : null,
      hasDiscount: has,
      isPriceRow: true,
    },
    { label: "Existencia", value: Number(data.value?.stock_qty || 0) },
    { label: "Categoría", value: data.value?.category || "General" },
  ];
});

async function load() {
  if (!props.productId) return;
  loading.value = true;
  try {
    const d = await getProductDetails(props.productId);
    data.value = d;
    mainImage.value = d?.images?.[0] || null;
  } finally {
    loading.value = false;
  }
}

watch(() => props.productId, load, { immediate: true });

function close() {
  emit("close");
}
function choose(img) {
  mainImage.value = img;
}
function addSelected() {
  if (!data.value) return;
  // Normalizar cantidad antes de agregar
  const n = Math.max(1, Math.min(maxQty.value, Number(qty.value || 1)));
  qty.value = n;
  addToCart(
    {
      id: data.value.id,
      name: data.value.name,
      unit_price: Number(priceWithDiscount.value || data.value.unit_price || 0),
      image_base64: mainImage.value?.image_base64 || null,
    },
    n
  );
  // Abrir carrito y cerrar modal inmediatamente
  cartState.open = true;
  emit("close");
}
</script>

<template>
  <div v-if="open" class="overlay" @click="close"></div>
  <section
    v-if="open"
    class="modal"
    role="dialog"
    aria-modal="true"
    aria-label="Detalles del producto"
  >
    <header class="top">
      <button class="icon-btn" @click="close" aria-label="Cerrar">
        <span class="material-symbols-rounded">arrow_back</span>
      </button>
      <h3 class="title">Detalles del producto</h3>
      <div class="spacer"></div>
    </header>
    <div class="body" v-if="!loading && data">
      <div class="left">
        <div class="main">
          <img
            v-if="mainImage"
            :src="`data:${mainImage.mime};base64,${mainImage.image_base64}`"
            alt="imagen principal"
          />
        </div>
        <div class="thumbs" v-if="data.images?.length">
          <button
            v-for="img in data.images"
            :key="img.image_base64"
            class="thumb"
            @click="choose(img)"
          >
            <img
              :src="`data:${img.mime};base64,${img.image_base64}`"
              alt="thumbnail"
            />
          </button>
        </div>
        <!-- Chips superiores: SKU, Stock, Precio -->
        <div class="chip-row">
          <div v-for="(c, i) in topChips" :key="i" class="chip soft">
            <span class="chip-label">{{ c.label }}</span>
            <span class="chip-value">{{ c.value }}</span>
          </div>
        </div>
      </div>
      <div class="right">
        <div class="name">{{ data.name }}</div>
        <div class="price">
          <span v-if="hasDiscount" class="old"
            >${{ Number(data.unit_price || 0).toFixed(2) }}</span
          >
          <span class="new"
            >${{ Number(priceWithDiscount || 0).toFixed(2) }}</span
          >
          <span v-if="hasDiscount" class="badge"
            >-{{ Number(data.discount || 0) }}%</span
          >
        </div>
        <div class="meta">
          <span v-if="data.category">{{ data.category }}</span>
          <span>• Stock: {{ data.stock_qty }}</span>
          <span v-if="data.tax_rate != null"
            >• Impuesto: {{ data.tax_rate }}%</span
          >
          <span v-if="data.sku">• SKU: {{ data.sku }}</span>
        </div>

        <div v-if="data.description" class="desc">
          <div class="label">Descripción</div>
          <ul v-if="descBullets.length" class="bullets">
            <li v-for="(b, i) in descBullets" :key="i">{{ b }}</li>
          </ul>
          <p v-else>{{ data.description }}</p>
        </div>

        <div v-if="data.variants?.length" class="chips">
          <div class="label">Variantes</div>
          <div class="chip-row">
            <span
              v-for="v in data.variants"
              :key="v.id"
              class="chip"
              :class="{ off: !v.available }"
              >{{ v.name }}</span
            >
          </div>
        </div>

        <div v-if="data.portions?.length" class="chips">
          <div class="label">
            Tamaño de porción
            <span class="only">• Sólo {{ data.stock_qty }} en stock</span>
          </div>
          <div class="chip-row">
            <span v-for="s in data.portions" :key="s.id" class="chip">{{
              s.name
            }}</span>
          </div>
        </div>

        <div v-if="data.ingredients?.length" class="ingredients">
          <div class="label">Ingredientes</div>
          <ul>
            <li v-for="ing in data.ingredients" :key="ing.name">
              {{ ing.name }}
              <span v-if="ing.is_allergen" class="allergen">(alérgeno)</span>
            </li>
          </ul>
        </div>

        <div v-if="data.preparation" class="prep">
          <div class="label">Información de preparación</div>
          <div class="row">
            <span v-if="data.preparation.origin"
              >Origen: {{ data.preparation.origin }}</span
            >
            <span
              >• ETA: {{ data.preparation.eta_min_minutes }}–{{
                data.preparation.eta_max_minutes
              }}
              min</span
            >
          </div>
        </div>

        <div v-if="data.reviews?.length" class="reviews">
          <div class="label">Reviews</div>
          <div
            class="review"
            v-for="rv in data.reviews.slice(0, 3)"
            :key="rv.created_at"
          >
            <span class="stars">★ {{ rv.rating }}</span>
            <span class="author">{{ rv.author || "Anónimo" }}</span>
            <span class="comment">{{ rv.comment }}</span>
          </div>
        </div>

        <div class="actions">
          <label class="qty-label" for="qty-input">Cantidad</label>
          <input
            id="qty-input"
            class="qty-input"
            type="number"
            min="1"
            :max="maxQty"
            v-model.number="qty"
          />
          <button
            class="btn primary compact xs"
            :class="{ success: justAdded }"
            @click="addSelected"
          >
            <span class="material-symbols-rounded">shopping_bag</span>
            {{ justAdded ? "producto agregado.." : "Agregar al carrito" }}
          </button>
        </div>
      </div>
      <!-- Tabla de detalles al final, abarca todo el ancho de la modal -->
      <div class="details-table full-width">
        <div class="row" v-for="(d, i) in detailsTable" :key="i">
          <div class="label">{{ d.label }}</div>
          <div class="value">
            <template v-if="d.isPriceRow && d.hasDiscount">
              <span class="old-value">{{ d.old }}</span>
              <span class="new-value">{{ d.value }}</span>
            </template>
            <template v-else>{{ d.value }}</template>
          </div>
        </div>
        <!-- Total local: refleja cantidad x precio actual -->
        <div class="row total-row">
          <div class="label">Total</div>
          <div class="value">${{ Number(selectedTotal || 0).toFixed(2) }}</div>
        </div>
      </div>
    </div>
    <div v-else class="loading">Cargando…</div>
  </section>
</template>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 10000;
}
.modal {
  position: fixed;
  inset: 40px;
  background: var(--panel-bg);
  border: 1px solid var(--panel-border);
  border-radius: 16px;
  display: grid;
  grid-template-rows: auto 1fr;
  z-index: 10001;
  overflow: hidden;
}
.top {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem;
  border-bottom: 1px solid var(--panel-border);
}
.title {
  font-weight: 800;
}
.icon-btn {
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 10px;
  padding: 0.35rem;
  color: var(--text);
}
.body {
  display: grid;
  grid-template-columns: minmax(0, 420px) minmax(0, 1fr);
  gap: 1rem;
  padding: 1rem;
  align-items: start;
  overflow: auto;
  min-height: 0;
}
.left {
  display: grid;
  gap: 0.2rem;
  align-content: start;
  justify-items: start;
}
.main {
  display: block;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 0;
  height: auto;
  width: auto;
  max-width: 100%;
}
.main img {
  display: block;
  width: 100%;
  height: auto;
  border-radius: 12px;
}
.thumbs {
  display: grid;
  grid-auto-flow: column;
  gap: 0.25rem;
  overflow-x: auto;
  margin-top: 0;
  justify-content: start;
}
.thumb {
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 0;
  display: inline-block;
}
.thumb img {
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: 10px;
  display: block;
}
.right {
  display: grid;
  gap: 0.5rem;
  width: 100%; /* asegura que el contenido ocupe el ancho disponible */
}
.name {
  font-weight: 800;
  font-size: 1.1rem;
}
.price {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 800;
  font-size: 1.15rem;
}
.price .old {
  text-decoration: line-through;
  color: var(--muted);
  font-weight: 600;
}
.price .new {
  font-size: 1.25rem;
}
.price .badge {
  background: #ef4444;
  color: #fff;
  border-radius: 999px;
  padding: 0.1rem 0.45rem;
  font-size: 0.8rem;
}
.meta {
  color: var(--muted);
}
.low-stock-badge {
  margin-left: 0.4rem;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  background: #f59e0b; /* amber */
  color: #1c2027; /* mejor contraste en claro */
}
@media (prefers-color-scheme: dark) {
  .low-stock-badge {
    color: #fff;
  }
}
.desc .label {
  font-weight: 700;
  margin-bottom: 0.2rem;
}
.desc p {
  margin: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.bullets {
  margin: 0.25rem 0 0;
  padding-left: 1rem;
}
.bullets li {
  margin: 0.2rem 0;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.chips .label,
.ingredients .label,
.prep .label,
.reviews .label {
  font-weight: 700;
  margin-bottom: 0.2rem;
}
.chip-row {
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
}
.chip {
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 0.2rem 0.5rem;
}
.chip.soft {
  background: var(--surface);
}
.chip .chip-label {
  color: var(--muted);
  margin-right: 0.15rem;
}
.chips .only {
  color: var(--muted);
  font-weight: 500;
}
.chip.off {
  opacity: 0.5;
}
.ingredients ul {
  display: grid;
  gap: 0.2rem;
}
.allergen {
  color: #ef4444;
}
.reviews .review {
  display: grid;
  grid-template-columns: 60px 1fr;
  gap: 0.2rem;
  align-items: center;
}
.reviews .stars {
  color: #f59e0b;
  font-weight: 700;
}
.actions {
  display: flex;
  gap: 0.6rem;
  align-items: center;
}
.qty-label {
  font-weight: 600;
}
.qty-input {
  width: 90px;
  padding: 0.28rem 0.4rem;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--surface);
}
.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  border-radius: 12px;
  padding: 0.32rem 0.58rem;
  font-size: 0.86rem;
  cursor: pointer;
  transition: transform 160ms ease, box-shadow 160ms ease, background 160ms ease;
}
.btn.primary {
  background: #2563eb; /* Azul como el de las cards */
  color: #ffffff;
  border: none;
  box-shadow: 0 8px 20px rgba(37, 99, 235, 0.25);
}
.btn.primary:hover {
  transform: translateY(-1px) scale(1.03);
  box-shadow: 0 12px 28px rgba(37, 99, 235, 0.35);
}
.btn.primary.success {
  background: #10b981; /* Verde éxito temporal */
  box-shadow: 0 12px 28px rgba(16, 185, 129, 0.35);
}
.btn.primary.compact {
  padding: 0.32rem 0.58rem;
}
.btn.primary.compact.xs {
  padding: 0.26rem 0.5rem;
}
.details-table {
  border-top: 1px solid var(--panel-border);
  margin-top: 0.2rem;
}
.details-table.full-width {
  grid-column: 1 / -1; /* ocupar ambas columnas del grid de la modal */
}
.details-table .row {
  display: flex;
  justify-content: space-between;
  padding: 0.35rem 0;
  border-bottom: 1px dashed var(--panel-border);
}
.details-table .label {
  color: var(--muted);
}
.details-table .value {
  font-weight: 600;
}
.details-table .old-value {
  text-decoration: line-through;
  color: var(--muted);
  margin-right: 0.4rem;
  font-weight: 500;
}
.details-table .new-value {
  font-weight: 700;
}
.loading {
  display: grid;
  place-items: center;
  padding: 1rem;
}
@media (max-width: 900px) {
  .modal {
    inset: 0;
    border-radius: 0;
  }
  .body {
    grid-template-columns: 1fr;
  }
}
/* Responsive refinements */
@media (max-width: 1400px) {
  .body {
    grid-template-columns: 380px 1fr;
    gap: 0.7rem;
    padding: 0.7rem;
  }
}
@media (max-width: 1200px) {
  .body {
    grid-template-columns: 320px 1fr;
    gap: 0.6rem;
    padding: 0.6rem;
  }
}
@media (max-width: 800px) {
  .thumb img {
    width: 48px;
    height: 48px;
  }
}
</style>
